import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useGoogleSheets } from './hooks/useGoogleSheets';
import { retryAPICall } from './utils/api';
import {
  groupSlotsByField,
  findStackingOpportunities,
  isAtPreferredTime,
  isAdjacentToPreferredTime,
  normalizeDate,
} from './utils/timeline';
import { exportProcessingToCSV } from './utils/export';
import { buildEmailSentMap, buildRequestsRows } from './utils/sheetRows';
import { getRequestKey, newRequestId } from './utils/requestKey';
import { determineRefereePattern } from './utils/refereePattern';
import { SetupForm } from './components/SetupForm';
import { RequestPanel } from './components/RequestPanel';
import { TimelinePanel } from './components/TimelinePanel';
import { RequestFilters } from './components/RequestFilters';
import { Button, IconButton, Card, Input } from './design-system/components';
import sqViking from './design-system/assets/sqViking.png';

// Release any available-tab slots currently booked by this request (matched by
// its request key — see utils/requestKey.js). Shared by assign/unassign/deny/
// cancel so a request never leaves an orphaned booking.
const releaseSlotsFor = (available, requestKey) =>
  available.map((slot) =>
    slot.BookingRequestID === requestKey
      ? { ...slot, IsBooked: 'FALSE', BookingRequestID: '' }
      : slot
  );

// Merges `patch` into the single request matching `requestKey`, leaving every
// other request untouched. Shared by every mutation handler so the
// find-by-key logic lives in one place.
const patchRequest = (requests, requestKey, patch) =>
  requests.map((req) => (getRequestKey(req) === requestKey ? { ...req, ...patch } : req));

// Splits a Game Number like "42-r1" into [base, revision] for sorting, so
// clones (42 -> 42-r1 -> 42-r2) sort next to their original instead of by
// parseInt's truncated value alone (which ties all revisions to their base).
const parseGameNumberForSort = (gameNumber) => {
  const match = String(gameNumber || '').match(/^(\d+)(?:-r(\d+))?/);
  if (!match) return [0, 0];
  return [parseInt(match[1], 10) || 0, match[2] ? parseInt(match[2], 10) : 0];
};

// Statuses that end a request's lifecycle: entering one must release any booked
// slot and clear the assignment (see releaseSlotsFor). Defined as a set so a new
// terminal status can't silently skip slot release and re-introduce the orphaned-
// booking bug. (Cancelled has its own handler with extra bookkeeping, but is
// listed here so it's still handled correctly if ever routed through status change.)
const TERMINAL_STATUSES = new Set(['Denied', 'Cancelled']);

function App() {
  // Configuration (persisted)
  const [apiKey, setApiKey] = useLocalStorage('fieldScheduler_apiKey', '');
  const [clientId, setClientId] = useLocalStorage('fieldScheduler_clientId', '');
  const [spreadsheetId, setSpreadsheetId] = useLocalStorage('fieldScheduler_spreadsheetId', '');
  const [webhookUrl, setWebhookUrl] = useLocalStorage('fieldScheduler_webhookUrl', '');
  const [webhookSecret, setWebhookSecret] = useLocalStorage('fieldScheduler_webhookSecret', '');
  const [theme, setTheme] = useLocalStorage('fieldScheduler_theme', 'light');

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // Google auth hook
  const { isSignedIn, gapiLoaded, gisLoaded, error, signIn, signOut, refreshToken } = useGoogleSheets(apiKey, clientId);

  // Data state
  const [requests, setRequests] = useState([]);
  const [available, setAvailable] = useState([]);
  const [venues, setVenues] = useState([]);
  const [formResponses, setFormResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  // Whether Process_Requests has a 'Request ID' header (set during load).
  // Gates UUID assignment on import/clone — see the comment in loadData.
  const [sheetHasRequestIdColumn, setSheetHasRequestIdColumn] = useState(false);

  // UI state
  const [showSettings, setShowSettings] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestViewMode, setRequestViewMode] = useState('standard'); // 'standard' or 'verify'
  const [cloningRequest, setCloningRequest] = useState(null);
  const [collapsedFields, setCollapsedFields] = useState(new Set());
  const copyInProgressRef = useRef(false);
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'gameNumber'

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [venueFilter, setVenueFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedVenues, setSelectedVenues] = useState(new Set());

  // Pagination (from 04-04)
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsPerPage] = useState(25);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, dateFilter, venueFilter, searchTerm, requestViewMode]);

  // Auto-initialize selectedVenues when a request is selected
  useEffect(() => {
    if (selectedRequest) {
      const requestedVenue = selectedRequest['Requested Venue'];
      if (requestedVenue) {
        setSelectedVenues(new Set([requestedVenue]));
      } else {
        setSelectedVenues(new Set());
      }
    } else {
      setSelectedVenues(new Set());
    }
  }, [selectedRequest]);

  // Unique values for filters
  const uniqueDates = useMemo(() =>
    [...new Set(requests.map((r) => r['Requested Date']).filter(Boolean))].sort(),
    [requests]
  );

  const uniqueVenues = useMemo(() =>
    [...new Set(requests.map((r) => r['Requested Venue']).filter(Boolean))].sort(),
    [requests]
  );

  // Request stats
  const requestStats = useMemo(() => {
    const requested = requests.filter((r) => r['Request Status'] === 'Requested').length;
    const processing = requests.filter((r) => r['Request Status'] === 'Processing').length;
    const scheduled = requests.filter((r) => r['Request Status'] === 'Scheduled').length;
    const availableSlots = available.filter((s) => s.IsBooked === 'FALSE').length;

    return { requested, processing, scheduled, availableSlots };
  }, [requests, available]);

  // Filtered requests
  const filteredRequests = useMemo(() => {
    const filtered = requests.filter((r) => {
      // Status filter
      // Always hide Cancelled/Denied unless explicitly viewing that status or All
      if (statusFilter !== 'all' && statusFilter !== 'Cancelled') {
        if (r['Request Status'] === 'Cancelled') return false;
      }
      if (statusFilter !== 'all' && statusFilter !== 'Denied') {
        if (r['Request Status'] === 'Denied') return false;
      }

      if (statusFilter !== 'all') {
        if (statusFilter === 'Stale') {
          // Stale: Processing for 5+ days
          if (r['Request Status'] !== 'Processing') return false;
          const lastUpdated = r['Last Updated'] || r.Timestamp;
          if (lastUpdated) {
            const now = new Date();
            const updated = new Date(lastUpdated);
            const days = Math.floor((now - updated) / (1000 * 60 * 60 * 24));
            if (days < 5) return false;
          }
        } else if (r['Request Status'] !== statusFilter) {
          return false;
        }
      }

      // Date filter
      if (dateFilter && r['Requested Date'] !== dateFilter) return false;

      // Venue filter
      if (venueFilter !== 'all' && r['Requested Venue'] !== venueFilter) return false;

      // Search term
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const teamName = (r['Team Name'] || '').toLowerCase();
        const contactName = (r['Contact Name'] || '').toLowerCase();
        if (!teamName.includes(search) && !contactName.includes(search)) return false;
      }

      return true;
    });

    // Sort the filtered results
    return filtered.sort((a, b) => {
      if (sortBy === 'gameNumber') {
        // Game numbers carry revision suffixes from cloning (42 -> 42-r1 -> 42-r2).
        // Sort by base number first, then by revision, so revisions land next
        // to their original instead of ordering arbitrarily on a tied base number.
        const [aBase, aRev] = parseGameNumberForSort(a['Game Number']);
        const [bBase, bRev] = parseGameNumberForSort(b['Game Number']);
        return aBase - bBase || aRev - bRev;
      } else {
        // Sort by date
        const aDate = new Date(a['Requested Date'] || 0);
        const bDate = new Date(b['Requested Date'] || 0);
        return aDate - bDate;
      }
    });
  }, [requests, statusFilter, dateFilter, venueFilter, searchTerm, sortBy]);

  // Requests actually shown in the panel: in Verify mode, only Processing
  // requests are relevant, so filter down to that *before* paginating —
  // otherwise the pager (and "N total requests" count) reflects the full
  // filtered set while pages of non-Processing requests render empty.
  const displayRequests = useMemo(() => {
    if (requestViewMode === 'verify') {
      return filteredRequests.filter((r) => r['Request Status'] === 'Processing');
    }
    return filteredRequests;
  }, [filteredRequests, requestViewMode]);

  // Paginated requests
  const currentRequests = useMemo(() => {
    const start = (currentPage - 1) * requestsPerPage;
    return displayRequests.slice(start, start + requestsPerPage);
  }, [displayRequests, currentPage, requestsPerPage]);

  const totalPages = Math.ceil(displayRequests.length / requestsPerPage);

  // Grouped slots for timeline
  const groupedSlots = useMemo(() => {
    return groupSlotsByField(
      available,
      selectedRequest,
      selectedDate,
      venueFilter,
      uniqueDates,
      selectedVenues
    );
  }, [available, selectedRequest, selectedDate, venueFilter, uniqueDates, selectedVenues]);

  // Set initial selected date
  useEffect(() => {
    if (uniqueDates.length > 0 && !selectedDate) {
      setSelectedDate(uniqueDates[0]);
    }
  }, [uniqueDates, selectedDate]);

  // Data loading
  const loadData = useCallback(async () => {
    if (!isSignedIn || !spreadsheetId) return;

    setLoading(true);
    try {
      // Load form responses (optional - may not exist)
      try {
        const formResponsesResponse = await retryAPICall(() =>
          window.gapi.client.sheets.spreadsheets.values.get({
            spreadsheetId: spreadsheetId,
            range: 'Form_Responses!A:T',
          })
        , 3, 1000, refreshToken);

        const formResponsesData = formResponsesResponse.result.values;
        if (formResponsesData && formResponsesData.length > 0) {
          const headers = formResponsesData[0];
          const rows = formResponsesData.slice(1).map((row) => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] || '';
            });
            return obj;
          }).filter(r => r.Timestamp); // Only keep rows with timestamps

          setFormResponses(rows);
        }
      } catch {
        console.log('No Form_Responses sheet found (optional). Note: Case-sensitive check failed for "Form_Responses".');
        setFormResponses([]);
      }

      // Load requests from Process_Requests sheet
      // Range extends past Z to AE to cover the Last Updated column (AA) and
      // leave headroom for future columns without another range edit.
      const requestsResponse = await retryAPICall(() =>
        window.gapi.client.sheets.spreadsheets.values.get({
          spreadsheetId: spreadsheetId,
          range: 'Process_Requests!A:AE',
        })
      , 3, 1000, refreshToken);

      const requestsData = requestsResponse.result.values;
      if (requestsData && requestsData.length > 0) {
        const headers = requestsData[0];
        // Only assign Request IDs to new imports/clones if the sheet can
        // actually store them — otherwise a UUID key would book slots in
        // this session, get dropped on write (no matching header), and
        // orphan the booking after the next reload.
        setSheetHasRequestIdColumn(headers.includes('Request ID'));
        const rows = requestsData.slice(1).map((row, idx) => {
          const obj = { RowIndex: idx + 2 }; // Row 1 is headers, so data starts at row 2
          headers.forEach((header, index) => {
            obj[header] = row[index] || '';
          });
          return obj;
        });
        setRequests(rows);
      }

      // Load available slots
      const availableResponse = await retryAPICall(() =>
        window.gapi.client.sheets.spreadsheets.values.get({
          spreadsheetId: spreadsheetId,
          range: 'available!A:Z',
        })
      , 3, 1000, refreshToken);

      const availableData = availableResponse.result.values;
      if (availableData && availableData.length > 0) {
        const headers = availableData[0];
        const rows = availableData.slice(1).map((row) => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] || '';
          });
          return obj;
        });
        setAvailable(rows);

        // Extract unique venues
        const venueSet = new Set(rows.map((r) => r.Venue).filter(Boolean));
        setVenues([...venueSet].sort());
      }
    } catch (err) {
      console.error('Load error details:', err);
      let errorMsg = err.message;

      // Handle GAPI style error objects
      if (!errorMsg && err.result?.error?.message) {
        errorMsg = err.result.error.message;
      }

      // Fallback for objects that don't have a message
      if (!errorMsg) {
        errorMsg = typeof err === 'string' ? err : JSON.stringify(err);
      }

      let hint = '';
      if (errorMsg.includes('not found') || errorMsg.includes('404')) {
        hint = '\n\nHint: Verify that your Spreadsheet ID is correct and that you have tabs named "Process_Requests" and "available".';
      }

      alert('Error loading data: ' + errorMsg + hint);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, spreadsheetId, refreshToken]);

  useEffect(() => {
    if (isSignedIn) {
      loadData();
    }
  }, [isSignedIn, loadData]);

  // Update sheets (save changes back to Google Sheets)
  // Returns true only if the write succeeded, so callers can gate their
  // success UI (alerts, selection updates) on it instead of assuming success.
  const updateSheets = useCallback(
    async (updatedRequests, updatedAvailable) => {
      if (!spreadsheetId) return false;

      // SAFETY CHECK: Don't write empty data
      if (!updatedRequests || updatedRequests.length === 0) {
        console.error('⚠️ Prevented writing empty requests to sheet!');
        alert('Error: Cannot save - no requests data. Your sheet was NOT modified.');
        return false;
      }

      try {
        // Read both tabs before writing:
        //  - Process_Requests full data, so we can preserve Email Sent values the
        //    Apps Script polling may have written since our last load.
        //  - available header row, so we write columns in the sheet's own order.
        const [requestsData, availableData] = await Promise.all([
          retryAPICall(() =>
            window.gapi.client.sheets.spreadsheets.values.get({
              spreadsheetId: spreadsheetId,
              range: 'Process_Requests!A:AE',
            })
          , 3, 1000, refreshToken),
          retryAPICall(() =>
            window.gapi.client.sheets.spreadsheets.values.get({
              spreadsheetId: spreadsheetId,
              range: 'available!A1:Z1',
            })
          , 3, 1000, refreshToken),
        ]);

        const allCurrentValues = requestsData.result.values || [[]];
        const headers = allCurrentValues[0] || [];

        // Guard against saving from a session opened BEFORE migrateToRequestIds
        // ran: if the sheet has gained a Request ID column since we loaded, our
        // in-memory rows are still Timestamp-keyed and a full-range write would
        // blank the new IDs (and revert available!BookingRequestID), undoing the
        // migration. Refuse and tell the admin to reload. (Normal sessions load
        // with the column present, so sheetHasRequestIdColumn is already true.)
        if (headers.includes('Request ID') && !sheetHasRequestIdColumn) {
          alert(
            'This sheet was migrated (a Request ID column was added) while the app was open.\n\n' +
            'Reload the page before saving so your changes use the new IDs. ' +
            'Your sheet was NOT modified.'
          );
          return false;
        }

        const sheetEmailSentMap = buildEmailSentMap(allCurrentValues, headers);
        const rows = buildRequestsRows(updatedRequests, headers, sheetEmailSentMap);

        const availableHeaders = availableData.result.values[0];
        const availableRows = [
          availableHeaders,
          ...updatedAvailable.map((slot) =>
            availableHeaders.map((h) => slot[h] || '')
          ),
        ];

        // Write both tabs in a single batchUpdate so a mid-save failure can't leave
        // a request marked Processing while its slot stays open (or vice versa).
        // valueInputOption RAW (not USER_ENTERED): cells are written as literal
        //   text, so Sheets never re-parses a timestamp/date into a serial whose
        //   display format could drift from the string the app compares against.
        //   The read side uses FORMATTED_VALUE, so what we write is exactly what
        //   we read back — a stable round-trip for every join value.
        // NOTE: a small residual race remains — the Email Sent read above and this
        //   write are not transactional, so an Apps Script poll landing in that window
        //   could still be clobbered. Acceptable for a single admin; batching keeps
        //   the window as small as possible.
        await retryAPICall(() =>
          window.gapi.client.sheets.spreadsheets.values.batchUpdate({
            spreadsheetId: spreadsheetId,
            resource: {
              valueInputOption: 'RAW',
              data: [
                { range: 'Process_Requests!A:AE', values: rows },
                { range: 'available!A:Z', values: availableRows },
              ],
            },
          })
        , 3, 1000, refreshToken);

        // Trigger Apps Script email check via Web App webhook (fire and forget),
        // only after the write has succeeded. Secret goes in the query string
        // (not a JSON body) so the request stays a CORS "simple request" under no-cors mode.
        if (webhookUrl) {
          const url = webhookSecret
            ? `${webhookUrl}${webhookUrl.includes('?') ? '&' : '?'}secret=${encodeURIComponent(webhookSecret)}`
            : webhookUrl;
          fetch(url, { method: 'POST', mode: 'no-cors' }).catch(() => {});
        }

        setRequests(updatedRequests.map(req => ({
          ...req,
          'Email Sent': req['Email Sent'] === 'RESEND' ? '' : (req['Email Sent'] || ''),
        })));
        setAvailable(updatedAvailable);
        return true;
      } catch (err) {
        console.error('Update error:', err);
        // gapi failures are response objects, not Errors — the real reason
        // lives in result.error.message and err.message is undefined.
        const detail =
          err?.result?.error?.message ||
          err?.message ||
          (err?.status ? `HTTP ${err.status}` : 'unknown error');
        const authHint =
          err?.status === 401 || err?.status === 403
            ? '\n\nYour Google sign-in has likely expired — reload the page, sign in again, and retry. Your sheet was NOT modified.'
            : '';
        alert('Error updating sheets: ' + detail + authHint);
        return false;
      }
    },
    [spreadsheetId, webhookUrl, webhookSecret, refreshToken, sheetHasRequestIdColumn]
  );

  // Get next game number
  const getNextGameNumber = useCallback(() => {
    const numbers = requests
      .map((r) => parseInt(r['Game Number']) || 0)
      .filter((n) => n > 0);
    return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  }, [requests]);

  // Action handlers
  const handleAssignField = useCallback(
    async (request, slot) => {
      const slotKey = `${slot.Venue} - ${slot['Field #']} - ${slot.Date} - ${slot.Time}`;
      const gameNumber = request['Game Number'] || getNextGameNumber();

      const requestKey = getRequestKey(request);

      const updatedRequests = patchRequest(requests, requestKey, {
        SlotID: slotKey,
        'Request Status': 'Processing',
        Venue: slot.Venue,
        'Sub Venue': slot['Sub Venue'] || slot['Field #'] || '',
        'Field #': slot['Field #'],
        Date: slot.Date,
        Time: slot.Time,
        'Game Number': gameNumber,
        'Last Updated': new Date().toISOString(),
      });

      // Release any slot this request was already booked into (H1: reassigning
      // a Processing request must not orphan its previous slot), then book the new one.
      const releasedAvailable = releaseSlotsFor(available, requestKey);
      const updatedAvailable = releasedAvailable.map((s) =>
        s.Venue === slot.Venue &&
          s['Field #'] === slot['Field #'] &&
          normalizeDate(s.Date) === normalizeDate(slot.Date) &&
          s.Time === slot.Time
          ? { ...s, IsBooked: 'TRUE', BookingRequestID: requestKey }
          : s
      );

      const ok = await updateSheets(updatedRequests, updatedAvailable);
      if (ok) setSelectedRequest(updatedRequests.find(r => getRequestKey(r) === requestKey));
    },
    [requests, available, updateSheets, getNextGameNumber]
  );

  const handleChangeField = useCallback(
    async (request, newSlot) => {
      // Validate new slot is available
      const newSlotAvailable = available.find(
        (slot) =>
          slot.Venue === newSlot.Venue &&
          slot['Field #'] === newSlot['Field #'] &&
          normalizeDate(slot.Date) === normalizeDate(newSlot.Date) &&
          slot.Time === newSlot.Time &&
          slot.IsBooked === 'FALSE'
      );

      if (!newSlotAvailable) {
        alert('The selected slot is not available.');
        return;
      }

      const newSlotKey = `${newSlot.Venue} - ${newSlot['Field #']} - ${newSlot.Date} - ${newSlot.Time}`;
      const requestKey = getRequestKey(request);

      // Release old slot
      const updatedAvailableStep1 = releaseSlotsFor(available, requestKey);

      // Book new slot
      const updatedAvailable = updatedAvailableStep1.map((slot) =>
        slot.Venue === newSlot.Venue &&
          slot['Field #'] === newSlot['Field #'] &&
          normalizeDate(slot.Date) === normalizeDate(newSlot.Date) &&
          slot.Time === newSlot.Time
          ? { ...slot, IsBooked: 'TRUE', BookingRequestID: requestKey }
          : slot
      );

      const updatedRequests = patchRequest(requests, requestKey, {
        SlotID: newSlotKey,
        Venue: newSlot.Venue,
        'Sub Venue': newSlot['Sub Venue'] || newSlot['Field #'] || '',
        'Field #': newSlot['Field #'],
        Date: newSlot.Date,
        Time: newSlot.Time,
        'Last Updated': new Date().toISOString(),
      });

      const ok = await updateSheets(updatedRequests, updatedAvailable);
      if (ok) setSelectedRequest(updatedRequests.find(r => getRequestKey(r) === requestKey));
    },
    [requests, available, updateSheets]
  );

  const handleUnassign = useCallback(
    async (request) => {
      const requestKey = getRequestKey(request);

      // Release the slot
      const updatedAvailable = releaseSlotsFor(available, requestKey);

      // Clear assignment from request
      const updatedRequests = patchRequest(requests, requestKey, {
        SlotID: '',
        'Request Status': 'Requested',
        Venue: '',
        'Sub Venue': '',
        'Field #': '',
        Date: '',
        Time: '',
        'Last Updated': new Date().toISOString(),
      });

      const ok = await updateSheets(updatedRequests, updatedAvailable);
      if (ok) setSelectedRequest(null);
    },
    [requests, available, updateSheets]
  );

  const handleMarkScheduled = useCallback(
    async (request) => {
      const requestKey = getRequestKey(request);

      const updatedRequests = patchRequest(requests, requestKey, {
        'Request Status': 'Scheduled',
        'Last Updated': new Date().toISOString(),
      });

      const ok = await updateSheets(updatedRequests, available);
      if (ok) setSelectedRequest(updatedRequests.find(r => getRequestKey(r) === requestKey));
    },
    [requests, available, updateSheets]
  );

  const handleStatusChange = useCallback(
    async (request, newStatus, declineReason) => {
      const requestKey = getRequestKey(request);

      // Any terminal status must release the assigned field and clear the
      // assignment (H2) — otherwise the slot stays booked forever.
      const clearAssignment = TERMINAL_STATUSES.has(newStatus);
      const updatedAvailable = clearAssignment
        ? releaseSlotsFor(available, requestKey)
        : available;

      const updatedRequests = patchRequest(requests, requestKey, {
        'Request Status': newStatus,
        'Last Updated': new Date().toISOString(),
        // If declining with a reason, add it to Admin Notes
        ...(declineReason && { 'Admin Notes': declineReason }),
        ...(clearAssignment && {
          SlotID: '',
          Venue: '',
          'Sub Venue': '',
          'Field #': '',
          Date: '',
          Time: '',
        }),
      });

      await updateSheets(updatedRequests, updatedAvailable);
    },
    [requests, available, updateSheets]
  );

  const handleUpdateAdminNotes = useCallback(
    async (request, newNotes) => {
      const requestKey = getRequestKey(request);

      const updatedRequests = patchRequest(requests, requestKey, {
        'Admin Notes': newNotes,
        'Last Updated': new Date().toISOString(),
      });

      const ok = await updateSheets(updatedRequests, available);
      if (ok) setSelectedRequest(updatedRequests.find(r => getRequestKey(r) === requestKey));
    },
    [requests, available, updateSheets]
  );

  const handleSetRefereePattern = useCallback(
    async (request, pattern) => {
      const requestKey = getRequestKey(request);

      const updatedRequests = patchRequest(requests, requestKey, {
        'Referee Pattern': pattern,
        'Last Updated': new Date().toISOString(),
      });

      const ok = await updateSheets(updatedRequests, available);
      if (ok) setSelectedRequest(updatedRequests.find(r => getRequestKey(r) === requestKey));
    },
    [requests, available, updateSheets]
  );

  const handleResendEmail = useCallback(
    async (request) => {
      const requestKey = getRequestKey(request);

      const updatedRequests = patchRequest(requests, requestKey, {
        'Email Sent': 'RESEND',
        'Last Updated': new Date().toISOString(),
      });

      const ok = await updateSheets(updatedRequests, available);
      if (ok) alert('Email will be re-sent shortly.');
    },
    [requests, available, updateSheets]
  );

  const handleCancel = useCallback(
    async (request, reason) => {
      const requestKey = getRequestKey(request);

      // Release the slot if assigned
      const updatedAvailable = releaseSlotsFor(available, requestKey);

      // Clear assignment fields, set Cancelled, write reason to Admin Notes
      const updatedRequests = patchRequest(requests, requestKey, {
        'Request Status': 'Cancelled',
        SlotID: '',
        Venue: '',
        'Sub Venue': '',
        'Field #': '',
        Date: '',
        Time: '',
        'Admin Notes': reason,
        'Last Updated': new Date().toISOString(),
      });

      const ok = await updateSheets(updatedRequests, updatedAvailable);
      if (ok) setSelectedRequest(null);
    },
    [requests, available, updateSheets]
  );

  const handleVerificationConfirm = useCallback(
    async (request) => {
      await handleMarkScheduled(request);
    },
    [handleMarkScheduled]
  );

  const handleCloneSubmit = useCallback(
    async (updatedData) => {
      // Derive revised game number: 42 -> 42-r1, 42-r1 -> 42-r2, etc.
      const origGameNum = updatedData['Game Number'] || '';
      let revisedGameNum = '';
      if (origGameNum) {
        const revMatch = origGameNum.match(/^(.+)-r(\d+)$/);
        if (revMatch) {
          revisedGameNum = `${revMatch[1]}-r${parseInt(revMatch[2]) + 1}`;
        } else {
          revisedGameNum = `${origGameNum}-r1`;
        }
      }

      const newRequest = {
        ...updatedData,
        'Request Status': 'Requested',
        SlotID: '',
        'Last Updated': new Date().toISOString(),
        Timestamp: new Date().toISOString(),
        'Game Number': revisedGameNum,
        // The spread above copies the ORIGINAL's identity — both must be
        // replaced or the clone would collide with its source.
        'Request ID': sheetHasRequestIdColumn ? newRequestId() : '',
        RowIndex: undefined,
      };

      const updatedRequests = [...requests, newRequest];
      const ok = await updateSheets(updatedRequests, available);
      if (!ok) return; // keep the modal open so the admin can retry
      setCloningRequest(null);
      alert('New request created successfully!');
    },
    [requests, available, updateSheets, sheetHasRequestIdColumn]
  );

  // Copy new form responses to Process_Requests
  const copyNewResponsesToRequests = useCallback(async () => {
    if (copyInProgressRef.current) return;
    copyInProgressRef.current = true;

    try {
    const newResponses = formResponses.filter(
      fr => !requests.find(r => r.Timestamp === fr.Timestamp)
    );

    if (newResponses.length === 0) {
      alert('No new form responses to copy.');
      return;
    }

    const confirmMsg = `Copy ${newResponses.length} new form ${newResponses.length === 1 ? 'response' : 'responses'} to Process_Requests?`;
    if (!confirm(confirmMsg)) return;

    // Add new responses to requests with "Requested" status and referee pattern
    const newRequests = newResponses.map(response => {
      const refereePattern = determineRefereePattern(
        response['Game Type'],
        response['Age Group'],
        response['Field Size']
      );

      return {
        ...response,
        'Request Status': 'Requested',
        'Referee Pattern': refereePattern,
        'Last Updated': new Date().toISOString(),
        'Request ID': sheetHasRequestIdColumn ? newRequestId() : '',
      };
    });

    const updatedRequests = [...requests, ...newRequests];
    const ok = await updateSheets(updatedRequests, available);
    if (ok) {
      alert(`Successfully copied ${newResponses.length} new ${newResponses.length === 1 ? 'request' : 'requests'}!`);
    }
    } finally {
      copyInProgressRef.current = false;
    }
  }, [formResponses, requests, available, updateSheets, sheetHasRequestIdColumn]);

  // Helper functions for timeline
  const toggleFieldCollapse = useCallback((key) => {
    setCollapsedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const getAssignedRequest = useCallback(
    (slot) => {
      if (slot.IsBooked === 'TRUE' && slot.BookingRequestID) {
        return requests.find((r) => getRequestKey(r) === slot.BookingRequestID);
      }
      return null;
    },
    [requests]
  );

  // Drag and drop handlers
  const handleDrop = useCallback(
    (e, slot) => {
      e.preventDefault();
      e.currentTarget.classList.remove('drag-over');

      const requestKey = e.dataTransfer.getData('requestKey');
      const request = requests.find((r) => getRequestKey(r) === requestKey);

      if (request && slot.IsBooked === 'FALSE') {
        handleAssignField(request, slot);
      }
    },
    [requests, handleAssignField]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  // Setup check
  if (!apiKey || !clientId || !spreadsheetId) {
    return (
      <SetupForm
        apiKey={apiKey}
        setApiKey={setApiKey}
        clientId={clientId}
        setClientId={setClientId}
        spreadsheetId={spreadsheetId}
        setSpreadsheetId={setSpreadsheetId}
        webhookUrl={webhookUrl}
        setWebhookUrl={setWebhookUrl}
        webhookSecret={webhookSecret}
        setWebhookSecret={setWebhookSecret}
        onSubmit={() => { }}
      />
    );
  }

  const authReady = gapiLoaded && gisLoaded;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header style={{ background: 'var(--surface-card)', borderBottom: '2px solid var(--border)', boxShadow: 'var(--shadow-xs)' }}>
        <div className="max-w-[1920px] mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src={sqViking} alt="" width={32} height={32} style={{ display: 'block' }} />
            <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--text-xl)', color: 'var(--text-strong)' }}>
              Field Scheduler
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {loading && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Loading...</span>}
            {error && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--danger)' }}>{error}</span>}
            <IconButton
              icon={theme === 'dark' ? 'sun' : 'moon'}
              variant="ghost"
              size="sm"
              label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
            />
            {!authReady ? (
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Initializing...</span>
            ) : isSignedIn ? (
              <>
                {formResponses.filter(fr => !requests.find(r => r.Timestamp === fr.Timestamp)).length > 0 && (
                  <Button variant="accent" size="sm" iconLeft="inbox" onClick={copyNewResponsesToRequests}>
                    New Form Responses ({formResponses.filter(fr => !requests.find(r => r.Timestamp === fr.Timestamp)).length})
                  </Button>
                )}
                {requestStats.processing > 0 && (
                  <Button variant="secondary" size="sm" iconLeft="bar-chart-2" onClick={() => exportProcessingToCSV(requests)}>
                    Export Processing ({requestStats.processing})
                  </Button>
                )}
                <Button variant="outline" size="sm" iconLeft="refresh-cw" onClick={loadData}>
                  Refresh
                </Button>
                <div className="relative">
                  <IconButton
                    icon="settings"
                    variant="ghost"
                    size="sm"
                    label="Settings"
                    onClick={() => setShowSettings(s => !s)}
                  />
                  {showSettings && (
                    <Card className="absolute right-0 top-12 z-50 w-80" padding="var(--space-4)" style={{ boxShadow: 'var(--shadow-lg)' }}>
                      <div className="space-y-3">
                        <Input
                          label="Apps Script Webhook URL"
                          hint="Triggers email sending immediately after each save."
                          size="sm"
                          value={webhookUrl}
                          onChange={(e) => setWebhookUrl(e.target.value)}
                          placeholder="https://script.google.com/macros/s/.../exec"
                        />
                        <Input
                          label="Webhook Secret"
                          hint="Leave blank if the script doesn't enforce a secret."
                          size="sm"
                          value={webhookSecret}
                          onChange={(e) => setWebhookSecret(e.target.value)}
                          placeholder="Must match WEBHOOK_SECRET script property"
                        />
                      </div>
                    </Card>
                  )}
                </div>
                <Button variant="ghost" size="sm" iconLeft="log-out" onClick={signOut}>
                  Sign Out
                </Button>
              </>
            ) : (
              <Button variant="primary" size="sm" iconLeft="log-in" onClick={signIn}>
                Sign In with Google
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden p-4 max-w-[1920px] mx-auto w-full">
        {isSignedIn ? (
          <>
            {/* Filters */}
            <div className="mb-4">
              <RequestFilters
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                venueFilter={venueFilter}
                setVenueFilter={setVenueFilter}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                uniqueDates={uniqueDates}
                uniqueVenues={uniqueVenues}
                requestStats={requestStats}
              />
            </div>

            {/* Panels */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-hidden">
              <RequestPanel
                filteredRequests={displayRequests}
                currentRequests={currentRequests}
                selectedRequest={selectedRequest}
                setSelectedRequest={setSelectedRequest}
                requestViewMode={requestViewMode}
                setRequestViewMode={setRequestViewMode}
                sortBy={sortBy}
                setSortBy={setSortBy}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                requestsPerPage={requestsPerPage}
                onStatusChange={handleStatusChange}
                onUpdateAdminNotes={handleUpdateAdminNotes}
                onClone={(r) => setCloningRequest(r)}
                onUnassign={handleUnassign}
                onMarkScheduled={handleMarkScheduled}
                onCancel={handleCancel}
                onResendEmail={handleResendEmail}
                onSetRefereePattern={handleSetRefereePattern}
                onVerificationConfirm={handleVerificationConfirm}
                venues={venues}
                cloningRequest={cloningRequest}
                setCloningRequest={setCloningRequest}
                onCloneSubmit={handleCloneSubmit}
              />
              <TimelinePanel
                groupedSlots={groupedSlots}
                selectedRequest={selectedRequest}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                uniqueDates={uniqueDates}
                collapsedFields={collapsedFields}
                toggleFieldCollapse={toggleFieldCollapse}
                onAssign={handleAssignField}
                onChangeField={handleChangeField}
                onCancelBooking={handleCancel}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                findStackingOpportunities={findStackingOpportunities}
                getAssignedRequest={getAssignedRequest}
                isPreferredTime={isAtPreferredTime}
                isAdjacentTime={isAdjacentToPreferredTime}
                venues={venues}
                selectedVenues={selectedVenues}
                setSelectedVenues={setSelectedVenues}
              />
            </div>
          </>
        ) : !authReady ? (
          <div className="flex-1 flex items-center justify-center">
            <Card className="text-center">
              <div className="flex items-center justify-center mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: 'var(--secondary)' }}></div>
              </div>
              <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-muted)' }}>Loading Google authentication...</p>
            </Card>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <Card className="text-center">
              <p className="mb-4" style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-muted)' }}>Please sign in to continue.</p>
              <Button variant="primary" iconLeft="log-in" onClick={signIn}>
                Sign In with Google
              </Button>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
